import Image from "next/image"

import { Buttons } from "components/Button"
import Hero from "components/Hero"
import Layout, { Container, Section } from "components/Layout"
import SEO from "components/Seo"
import { Heading, Lead } from "components/Text"
import Usps from "components/Usps"
import fiveYearGuarantee from "public/5-year-guarantee.svg"
import sectionOneImg from "public/access-control-2.webp"
import sectionTwoImg from "public/access-control-3.webp"
import heroImg from "public/access-control.webp"
import sectionThreeImg from "public/maintenance-packages.webp"

import type { NextPage } from "next"

const SecurityGates: NextPage = () => {
  return (
    <>
      <SEO
        titleTag="Security Gates - 3H CCTV &amp; Alarms"
        metaDescription=""
        pageTitle="Security Gates"
      />
      <Layout>
        <Hero
          image={heroImg}
          imageClass="!object-center"
          imageAlt="Security Gates Derbyshire"
          heading="Security Gates"
          subHeading="Installation &amp; Repairs"
        />

        <Usps />

        <Section>
          <Container className="flex gap-6 smmax:flex-col-reverse flex-wrap mdmin:items-center">
            <div className="flex-1">
              <Heading>Domestic &amp; commercial security gates</Heading>
              <p>
                Secure your property and its contents with a security gate from
                3HCCTV. We supply and install a wide range of secure gates for
                both domestic and commercial customers across the Derbyshire,
                Nottinghamshire &amp; South Yorkshire regions.
              </p>
              <Buttons buttons={[{ kind: "phone" }, { kind: "email" }]} />
            </div>
            <Image
              src={sectionOneImg}
              sizes="(max-width: 748px) 80vw, 40vw"
              alt="Security Gates Derbyshire"
              placeholder="blur"
              className="mdmin:w-1/3 smmax:w-1/2 object-cover aspect-square"
            />
          </Container>
        </Section>

        <Section variant="gray-100">
          <Container className="flex gap-6 smmax:flex-col-reverse mdmin:flex-row-reverse flex-wrap mdmin:items-center">
            <div className="flex-1">
              <Heading>Domestic security gates</Heading>
              <p>
                3HCCTV supply, install and maintain the latest domestic security
                gates. Securing your home with a secure gate can help to protect
                the property, contents within and also any vehicles stored on
                the off-street parking. Our team of security gate experts can
                arrange a site visit and recommend the best gate to suit your
                requirements and budget.
              </p>
              <p>
                <strong>We supply and install:</strong>
              </p>
              <ul>
                <li>Swing security gates</li>
                <li>Sliding security gates</li>
                <li>Metal security gates</li>
                <li>Wooden security gates</li>
              </ul>
              <Buttons buttons={[{ kind: "phone" }, { kind: "email" }]} />
            </div>
            <Image
              src={sectionTwoImg}
              sizes="(max-width: 748px) 80vw, 40vw"
              alt="Domestic security gates Derbyshire"
              placeholder="blur"
              className="mdmin:w-1/3 smmax:w-1/2 object-cover aspect-square"
            />
          </Container>
        </Section>

        <Section>
          <Container className="flex gap-6 smmax:flex-col-reverse flex-wrap mdmin:items-center">
            <div className="flex-1">
              <Heading>Commercial security gates</Heading>
              <p>
                Our commercial security gate solutions are the best way to
                secure your commercial property. We supply, install and maintain
                the latest technology &amp; gates to ensure that your premises
                and the contents within are safe from intruders. Our team of
                commercial security gate experts can arrange a site visit and
                recommend the best security solution for your property and
                budget requirements.
              </p>
              <p>
                <strong>We supply and install:</strong>
              </p>
              <ul>
                <li>Swing security gates</li>
                <li>Sliding security gates</li>
                <li>Metal security gates</li>
                <li>Wooden security gates</li>
                <li>Security barriers</li>
                <li>Security bollards</li>
              </ul>
              <Buttons buttons={[{ kind: "phone" }, { kind: "email" }]} />
            </div>
            <Image
              src={sectionThreeImg}
              sizes="(max-width: 748px) 80vw, 40vw"
              alt="Commercial security gates Derbyshire"
              placeholder="blur"
              className="mdmin:w-1/3 smmax:w-1/2 object-cover aspect-square"
            />
          </Container>
        </Section>

        <Section variant="gray-100" className="text-center">
          <Container>
            <Image
              src={fiveYearGuarantee}
              alt="5 year guarantee"
              className="w-[300px] mx-auto"
            />
            <Heading
              underline={false}
              className="text-primary"
              alignment="center"
            >
              Our guarantee to you
            </Heading>
            <Lead>
              All of the domestic and commercial security gates that we supply
              come with a 5 year guarantee, giving you the peace of mind that
              the property and contents your security gates are protecting are
              always safe and secure.
            </Lead>
          </Container>
        </Section>
      </Layout>
    </>
  )
}

export default SecurityGates
